
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from '@/components/ui/textarea';
import { SaveIcon, BanIcon, Loader2Icon, DownloadIcon } from "@/components/Icons";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { generateGastoPDF } from '@/utils/pdfGenerator';
import { Gasto } from '@shared/types';
import { useUser } from '@/contexts/UserContext';

const createNewGasto = (): Partial<Gasto> => ({
    proveedor: '',
    cif: '',
    numeroFactura: '',
    fecha: new Date().toISOString().slice(0, 10),
    baseImponible: 0,
    tipoIva: 0.21,
    iva: 0,
    retencionIrpf: undefined,
    importeRetencion: undefined,
    total: 0,
    totalAPagar: 0,
    descripcion: '',
});

export default function GastoForm() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { fetchWithAuth } = useUser();
    const [gasto, setGasto] = useState<Partial<Gasto>>(createNewGasto());
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (id) {
            setIsLoading(true);
            const fetchGasto = async () => {
                try {
                    const response = await fetchWithAuth(`/api/gastos/${id}`);
                    if (!response.ok) throw new Error('Gasto no encontrado');
                    const data = await response.json();
                    setGasto(data);
                } catch (error) {
                    toast.error("Error al cargar gasto", { description: error instanceof Error ? error.message : 'Error desconocido' });
                    navigate('/gastos');
                } finally {
                    setIsLoading(false);
                }
            };
            fetchGasto();
        }
    }, [id, navigate, fetchWithAuth]);

    const handleFieldChange = useCallback((field: keyof Gasto, value: any) => {
        const newGasto = { ...gasto, [field]: value };

        // Recalcular totales si cambian campos fiscales
        const fieldsToRecalc = ['baseImponible', 'tipoIva', 'iva', 'retencionIrpf', 'importeRetencion'];
        if (fieldsToRecalc.includes(field)) {
            const base = field === 'baseImponible' ? Number(value) : Number(newGasto.baseImponible || 0);
            const tipoIva = field === 'tipoIva' ? Number(value) : Number(newGasto.tipoIva || 0.21);

            // Calcular IVA si cambia la base o el tipo
            let ivaImporte = newGasto.iva || 0;
            if (field === 'baseImponible' || field === 'tipoIva') {
                ivaImporte = Math.round(base * tipoIva * 100) / 100;
                newGasto.iva = ivaImporte;
            } else if (field === 'iva') {
                ivaImporte = Number(value);
            }

            // Calcular retención si cambia la base o el porcentaje
            const retencionPct = field === 'retencionIrpf' ? Number(value) || null : newGasto.retencionIrpf;
            let retencionImporte = newGasto.importeRetencion || null;
            if ((field === 'baseImponible' || field === 'retencionIrpf') && retencionPct) {
                retencionImporte = Math.round(base * retencionPct * 100) / 100;
                newGasto.importeRetencion = retencionImporte;
            } else if (field === 'importeRetencion') {
                retencionImporte = Number(value) || null;
            }

            // Total = Base + IVA (gasto contable)
            newGasto.total = Math.round((base + ivaImporte) * 100) / 100;
            // Total a Pagar = Base + IVA - Retención (lo que sale del banco)
            newGasto.totalAPagar = Math.round((newGasto.total - (retencionImporte || 0)) * 100) / 100;
        }
        setGasto(newGasto);
    }, [gasto]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const response = await fetchWithAuth(id ? `/api/gastos/${id}` : '/api/gastos', {
                method: id ? 'PUT' : 'POST',
                body: JSON.stringify(gasto),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al guardar');
            }
            toast.success(`Gasto ${id ? 'actualizado' : 'creado'} con éxito`);
            navigate('/gastos');
        } catch (error) {
            toast.error('Error al guardar', { description: error instanceof Error ? error.message : 'Ocurrió un error' });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) return <div className="flex justify-center items-center h-64"><Loader2Icon className="animate-spin h-8 w-8 text-primary" /></div>;

    return (
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-8">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold tracking-tighter">{id ? 'Editar Gasto' : 'Nuevo Gasto'}</h1>
                <div className="flex gap-2">
                    {id && (
                        <Button
                            variant="outline"
                            onClick={() => generateGastoPDF(gasto, `gasto-${gasto.numeroFactura || id}.pdf`)}
                        >
                            <DownloadIcon className="mr-2" />Descargar PDF
                        </Button>
                    )}
                    <Button variant="outline" onClick={() => navigate('/gastos')} disabled={isSaving}><BanIcon className="mr-2" />Cancelar</Button>
                    <Button onClick={handleSave} disabled={isSaving}><SaveIcon className="mr-2" />{isSaving ? 'Guardando...' : 'Guardar'}</Button>
                </div>
            </div>

            <Card>
                <CardHeader><CardTitle>Detalles del Gasto</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="proveedor">Proveedor</Label>
                        <Input id="proveedor" value={gasto.proveedor ?? ''} onChange={e => handleFieldChange('proveedor', e.target.value)} />
                    </div>
                     <div className="space-y-2">
                        <Label htmlFor="cif">CIF/NIF</Label>
                        <Input id="cif" value={gasto.cif ?? ''} onChange={e => handleFieldChange('cif', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="numeroFactura">Nº de Factura</Label>
                        <Input id="numeroFactura" value={gasto.numeroFactura ?? ''} onChange={e => handleFieldChange('numeroFactura', e.target.value)} />
                    </div>
                     <div className="space-y-2">
                        <Label htmlFor="fecha">Fecha</Label>
                        <Input id="fecha" type="date" value={gasto.fecha ?? ''} onChange={e => handleFieldChange('fecha', e.target.value)} />
                    </div>
                    <div className="space-y-2 md:col-span-3">
                        <Label htmlFor="descripcion">Descripción</Label>
                        <Textarea id="descripcion" value={gasto.descripcion ?? ''} onChange={e => handleFieldChange('descripcion', e.target.value)} placeholder="Descripción del gasto (opcional)"/>
                    </div>
                    {/* Fila: Base Imponible, Tipo IVA, IVA */}
                    <div className="space-y-2">
                        <Label htmlFor="baseImponible">Base Imponible (€)</Label>
                        <Input id="baseImponible" type="number" step="0.01" value={gasto.baseImponible ?? 0} onChange={e => handleFieldChange('baseImponible', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="tipoIva">Tipo IVA (%)</Label>
                        <select
                            id="tipoIva"
                            value={gasto.tipoIva ?? 0.21}
                            onChange={e => handleFieldChange('tipoIva', parseFloat(e.target.value))}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        >
                            <option value={0}>0% (Exento)</option>
                            <option value={0.04}>4% (Superreducido)</option>
                            <option value={0.10}>10% (Reducido)</option>
                            <option value={0.21}>21% (General)</option>
                        </select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="iva">IVA (€)</Label>
                        <Input id="iva" type="number" step="0.01" value={gasto.iva ?? 0} onChange={e => handleFieldChange('iva', e.target.value)} />
                    </div>

                    {/* Fila: Retención IRPF */}
                    <div className="space-y-2">
                        <Label htmlFor="retencionIrpf">Retención IRPF (%)</Label>
                        <select
                            id="retencionIrpf"
                            value={gasto.retencionIrpf ?? ''}
                            onChange={e => handleFieldChange('retencionIrpf', e.target.value ? parseFloat(e.target.value) : null)}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        >
                            <option value="">Sin retención</option>
                            <option value={0.07}>7% (Nuevos autónomos)</option>
                            <option value={0.15}>15% (General)</option>
                            <option value={0.19}>19% (Profesionales alta renta)</option>
                        </select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="importeRetencion">Retención (€)</Label>
                        <Input id="importeRetencion" type="number" step="0.01" value={gasto.importeRetencion ?? ''} onChange={e => handleFieldChange('importeRetencion', e.target.value)} placeholder="0.00" />
                    </div>
                    <div></div>

                    {/* Fila: Totales */}
                    <div className="space-y-2">
                        <Label htmlFor="total" className="text-muted-foreground">Total Gasto (€)</Label>
                        <Input id="total" type="number" value={gasto.total ?? 0} readOnly className="font-bold bg-muted/50" title="Base + IVA (gasto contable)" />
                        <p className="text-xs text-muted-foreground">Base + IVA</p>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="totalAPagar" className="text-primary font-medium">Total a Pagar (€)</Label>
                        <Input id="totalAPagar" type="number" value={gasto.totalAPagar ?? gasto.total ?? 0} readOnly className="font-bold bg-primary/10 border-primary" title="Lo que sale del banco" />
                        <p className="text-xs text-muted-foreground">Base + IVA - Retención</p>
                    </div>
                    <div></div>
                </CardContent>
            </Card>

            {/* Documento Original (si existe) */}
            {gasto.archivoOriginal && (
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2">Documento Original</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded">
                        <FileText className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{gasto.archivoOriginal.nombre}</p>
                        <p className="text-xs text-muted-foreground">
                          {(gasto.archivoOriginal.tamaño / 1024).toFixed(1)} KB ·
                          Subido {new Date(gasto.archivoOriginal.fechaSubida).toLocaleDateString('es-ES')}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <a href={gasto.archivoOriginal.url} target="_blank" rel="noopener noreferrer">
                        Ver Original
                      </a>
                    </Button>
                  </div>
                  {gasto.origen === 'IA' && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Este gasto fue creado automáticamente desde la imagen usando IA.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
        </div>
    );
}
